/**
 * Invoice Estimate Report — what the open book will bill vs what it bills today.
 *
 * The Pro Invoicing tab's "Invoice Estimate" answers this for one PRO. The reconciler
 * sweep publishes the same projection for every open order, and this page aggregates it,
 * so the question can be asked of the whole book: where are we about to under-bill?
 *
 * WHY THE HEADLINE ISN'T PROJECTED MINUS CURRENT. Across the open book that difference
 * reads in the millions, and most of it is fuel-surcharge lines sitting at rate 0 on
 * orders nobody has rated yet — unfinished, not unbilled, and they rate themselves at
 * billing time. The page headlines `delta_actionable` (missing + drift - overbilled) and
 * keeps Unrated as its own tile, next to the honest full delta. A report that leads with
 * a number people can immediately name as wrong doesn't get a second look.
 *
 * SCOPE IS OPEN ORDERS. The estimate cache only holds orders with an undated invoice, so
 * everything here is exposure that can still be PREVENTED — not history that was lost.
 * The freshness line says when the sweep last ran, because every figure is as-of that.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  ArrowTopRightOnSquareIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { TanTable } from '../../components/TanTable';
import type { CoveredFieldMap } from '../../components/proCardColumns';
import {
  useAdditionalFieldColumns,
  useAdditionalFieldSelection,
} from '../../hooks/useAdditionalFields';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import SingleSelect from '../../components/SingleSelect';
import FilterBar from '../../components/FilterBar';
import Toggle from '../../components/Toggle';
import Tile from '../../components/reports/ReportTile';
import DateTimePicker from '../../components/DateTimePicker';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useToast } from '../../contexts/ToastContext';
import invoiceEstimateReportService, {
  EstimateDetailRow,
  EstimateGroupBy,
  EstimateGroupRow,
  EstimateKind,
  EstimateMeasures,
  EstimateState,
  EstimateQuery,
  EstimateSummaryResponse,
  EstimateTotals,
  scopeQueryToGroup,
} from '../../services/invoiceEstimateReport.service';

const GROUP_BY_OPTIONS: { value: EstimateGroupBy; label: string }[] = [
  { value: 'customer', label: 'Customer' },
  { value: 'billto', label: 'Bill To' },
  { value: 'liitem', label: 'Line Item' },
  { value: 'kind', label: 'Difference Type' },
  { value: 'state', label: 'Completed / Pending' },
  { value: 'movetype', label: 'Move Type' },
];

/** The drill-down grid's key. TanTable saves its layout under it and the added-column
 *  selection is saved under the same name, so a layout and the columns it refers to
 *  cannot come apart. */
const DETAIL_SETTINGS_KEY = 'invoice-estimate-report-detail';

/**
 * Registry fields the drill-down already shows under its own column ids, so the picker
 * does not offer them a second time. Customer and Bill To here are the NAMES, which is
 * what `customername` / `billtoname` resolve to. The registry's `billto` — the bill-to
 * NUMBER — needs no entry: it shares the column's id, and the picker's own rules catch
 * that.
 */
const DETAIL_COVERED_FIELDS: CoveredFieldMap = {
  customer: ['customername'],
  billto: ['billtoname'],
};

/** Label + one-line explanation per difference type. The explanations are the whole
 *  reason the classification exists, so they travel with the value rather than living
 *  in a legend nobody opens. */
const KIND_META: Record<EstimateKind, { label: string; help: string; tone: string }> = {
  missing: {
    label: 'Missing charge',
    // Covers BOTH shapes since the fold: no line at all, and a line sitting at $0 with
    // real dates. The wording used to say "on no invoice at all", which stopped being
    // true the moment rate-less lines moved in here.
    help: 'The order earned this charge and the invoice bills none of it: either no '
        + 'line at all, or a line sitting at $0 that nobody has rated.',
    tone: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  },
  drift: {
    label: 'Rate drift',
    help: 'The line belongs on the invoice; only its rate disagrees with the quote. Can '
        + 'run either way, under or over billing, which is why it is kept apart from '
        + 'charges that should not be billed at all.',
    tone: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-200',
  },
  overbilled: {
    // NOT "Over-billed": rate drift can be over-billing too, when the invoice's rate is
    // higher than the quote's. This bucket is the whole CHARGE being wrong, not its rate.
    // And not "not on quote" either — only 2 of 162 are absent from the quote; the other
    // 160 are priced there and simply do not apply to this order (free days cover the
    // dwell, the dwell computes to zero). "Not owed" is true of both.
    label: 'Billed, not owed',
    help: "The invoice bills this charge but the quote's terms don't support it here: "
        + 'free days cover the dwell, the dwell is zero, or the quote prices no such '
        + 'charge. The whole charge comes off, not just its rate.',
    tone: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  },
  withheld: {
    label: 'Withheld',
    help: 'An override deliberately declined to bill this charge.',
    tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  },
  agreed: {
    label: 'In agreement',
    help: 'The invoice and the estimate already agree.',
    tone: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200',
  },
};

const fmt = (v: number | null | undefined, dp: number) => {
  const n = Number(v ?? 0);
  const body = Math.abs(n).toLocaleString(undefined, {
    minimumFractionDigits: dp, maximumFractionDigits: dp,
  });
  // Sign BEFORE the currency symbol. `$-740` is not how anyone writes money.
  return `${n < 0 ? '-' : ''}$${body}`;
};

/** The other axis: can the amount be trusted yet. Per LINE — a PRO can be completed for
 *  fuel (rated off a delivery that happened) and pending for chassis (waiting on a
 *  terminate date), so it appears under both. */
const STATE_META: Record<EstimateState, { label: string; help: string; tone: string }> = {
  completed: {
    label: 'Completed',
    help: 'Every date invoicing needs is on the order, so the amount is definitive.',
    tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  },
  pending: {
    label: 'Pending',
    help: "Using today's date to estimate final charges, because the order isn't done "
        + 'yet. The charge is real; the amount will settle when the real date lands '
        + '(delivery for fuel, terminate for chassis).',
    tone: 'bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200',
  },
};

const money = (v: number | null | undefined) => fmt(v, 0);
const money2 = (v: number | null | undefined) => fmt(v, 2);

const num = (v: number | null | undefined) => Number(v ?? 0).toLocaleString();

/** Local calendar date as YYYY-MM-DD. NOT toISOString(): that converts to UTC first, so
 *  a date picked west of UTC comes out as the previous day and quietly shifts the window. */
const ymd = (d: Date | null): string | undefined => {
  if (!d) return undefined;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/**
 * A signed difference. Colour marks the EXCEPTION, not the rule.
 *
 * Two passes got this wrong. Green for negatives read as good news, when a negative is
 * over-billing — charging a customer for something they do not owe, a customer-facing
 * error and arguably the worse of the two. Red for positives then coloured the entire
 * column, because under-billing is what the report is mostly made of, and a column that
 * is uniformly red says nothing while adding weight to the figures you scan most.
 *
 * So the common case is plain and the rare one carries the purple of "Billed, not owed".
 * The sign still shows direction; colour is spent only where it discriminates.
 *
 * Returns a plain span, not a component: TanTable reads the rendered text to sniff money
 * columns, and a nested component gives it nothing to read.
 */
/**
 * A bucket figure in its own colour: the column matches the card it came from, so the eye
 * can find "where is the rate drift" without reading headers. Zero is muted rather than
 * coloured, because a table where every cell is coloured has no signal in it — which is
 * what a flat monochrome table and an all-red one have in common.
 */
const bucketMoney = (value: number | null | undefined, tone: string) => {
  const v = Number(value ?? 0);
  if (v === 0) return <span className="font-mono text-gray-300 dark:text-gray-600">$0</span>;
  return <span className={`font-mono ${tone}`}>{money(v)}</span>;
};

/**
 * A signed figure on a column that runs BOTH ways, coloured by WHICH WAY THE MONEY GOES:
 * green when the estimate would bill MORE than the invoice does, red when it would bill
 * less. The figure is suggested minus current, so a positive is revenue to capture and a
 * negative is revenue to give back.
 *
 * Only Actionable and Rate drift qualify. Missing is one-directional by construction, so
 * a single colour says more there than a scale that never uses half its range.
 */
const signedMoney = (value: number | null | undefined) => {
  const v = Number(value ?? 0);
  if (v === 0) return <span className="font-mono text-gray-300 dark:text-gray-600">$0</span>;
  const cls = v > 0
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';
  return <span className={`font-mono ${cls}`}>{v > 0 ? '+' : ''}{money(v)}</span>;
};

/**
 * IN THE TABLE, COLOUR IS DIRECTION. Green is money the estimate would add, red is money
 * it would take away, and the one-directional columns follow the same rule their signed
 * neighbours do: Missing is always revenue to capture, Billed-not-owed is always revenue
 * to hand back. Colouring Missing red made it look like a loss in a column of gains, and
 * put it in the same ink as the figures that genuinely move the other way.
 *
 * The CARDS keep their own hues as bucket identity — that is a different question ("which
 * bucket is this") from the one the table answers ("which way does the money go").
 */
const BUCKET_TONE = {
  missing: 'text-green-600 dark:text-green-400',
  overbilled: 'text-red-600 dark:text-red-400',
  withheld: 'text-sky-700 dark:text-sky-300',
  pending: 'text-teal-700 dark:text-teal-300',
} as const;

const deltaSpan = (value: number) => {
  const v = Number(value ?? 0);
  if (v === 0) return <span className="font-mono text-gray-400">$0</span>;
  const cls = v > 0
    ? 'text-green-600 dark:text-green-400'
    : 'text-red-600 dark:text-red-400';
  return <span className={`font-mono font-semibold ${cls}`}>{v > 0 ? '+' : ''}{money(v)}</span>;
};

function InvoiceEstimateReportContent() {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [groupBy, setGroupBy] = useState<EstimateGroupBy>('customer');
  const [kind, setKind] = useState<EstimateKind[]>([]);
  /** Line-item selection, driven by the per-charge cards rather than a dropdown. */
  const [liitem, setLiitem] = useState<string[]>([]);
  const [state, setState] = useState<EstimateState[]>([]);
  /** "Include matched invoices" — one setting for the whole report, summary and
   *  drill-down alike. Off by default: what already matches is not what you came for. */
  const [includeMatched, setIncludeMatched] = useState(false);
  // Both null = every open invoice, no date filter. A null `to` with a set `from` also
  // includes containers not yet delivered — see the FilterBar comment.
  const [deliveredFrom, setDeliveredFrom] = useState<Date | null>(null);
  const [deliveredTo, setDeliveredTo] = useState<Date | null>(null);
  /** "Show Not Available" — off by default. Availability is a property of the ORDER
   *  (imports/exports: a GWI Available date; everything else: an originate-leg
   *  appointment — see the backend's _AVAILABLE_EXPR), so like the date window it
   *  scopes every figure on the page: cards, charge cards, table and drill-down. */
  const [showNotAvailable, setShowNotAvailable] = useState(false);

  const [summary, setSummary] = useState<EstimateSummaryResponse | null>(null);
  /** Totals the cards show: every type under the current DATE filter, never narrowed by
   *  the type selection — see load(). */
  const [cardTotals, setCardTotals] = useState<EstimateTotals | null>(null);
  /** One card per line item, under the date window. */
  const [itemCards, setItemCards] = useState<EstimateGroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down: the group row a user opened, and the lines behind it.
  const [drill, setDrill] = useState<EstimateGroupRow | null>(null);
  const [drillRows, setDrillRows] = useState<EstimateDetailRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillMeta, setDrillMeta] = useState<{ total: number; truncated: boolean }>(
    { total: 0, truncated: false });
  const [selectedPro, setSelectedPro] = useState<number | null>(null);

  /** Order fields the user added to the drill-down grid, from the whole orders
   *  registry. The same contract every other pro grid honours: the selection is saved
   *  under the grid's key, the names go up on the detail fetch as `additional_fields`,
   *  and the values come back attached to every line of that PRO. */
  const fieldSelection = useAdditionalFieldSelection(DETAIL_SETTINGS_KEY);
  /** The selection as one string — what the fetch keys on, so a render that hands back
   *  an equal list under a new identity can never refetch the drill-down. */
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  /** Stamp of the latest drill fetch, so a slower, superseded response cannot land on
   *  top of a newer one. The drill reloads from an effect — open a group, add a column,
   *  add another — and two of those in quick succession are routine, not an edge case. */
  const drillRequest = useRef(0);

  const query = useMemo<EstimateQuery>(
    () => ({
      kind,
      liitem,
      state,
      showNotAvailable,
      deliveredFrom: ymd(deliveredFrom),
      deliveredTo: ymd(deliveredTo),
    }),
    [kind, liitem, state, showNotAvailable, deliveredFrom, deliveredTo],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await invoiceEstimateReportService.summary(groupBy, query, 1000, includeMatched);
      setSummary(data);

      // THE CARDS MUST NOT MOVE WHEN YOU CLICK ONE. They are the menu you pick from, so
      // they show totals for every type under the date filter, NOT under the type filter
      // — otherwise selecting "Missing charges" zeroes every other card and there is
      // nothing left to switch to. Only costs a second call while a type is selected;
      // with none, the table's own totals already are the unfiltered ones.
      // Strip BOTH kind and state: the cards are the menu you pick from, so they report
      // the whole book under the date filter. Stripping only `kind` left the state filter
      // in place — and since a kind card also selects `completed`, choosing "Billed, not
      // owed" zeroed the Pending card and collapsed Full Difference to the completed
      // total. The menu destroyed the options it was offering.
      if (kind.length || state.length) {
        const base = await invoiceEstimateReportService.summary(
          groupBy, { ...query, kind: [], state: [] }, 1, false, true);
        setCardTotals(base.totals);
      } else {
        setCardTotals(data.totals);
      }

      // Line-item cards: grouped by liitem, ignoring the line-item selection for the same
      // reason — clicking CHASSIS MANAGEMENT must not blank every other charge's card.
      const items = await invoiceEstimateReportService.summary(
        'liitem', { ...query, liitem: [] }, 60, false, true);
      setItemCards(items.rows);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load the report');
    } finally {
      setLoading(false);
    }
  }, [groupBy, query, kind, state, includeMatched]);

  /** Clicking a card filters the report to that difference type; clicking it again clears
   *  it. The selection is the same server-side `kind` filter the drill-down inherits, so
   *  opening a group from a filtered report shows only that type's lines. */
  /** What a line-item card should show, given what is selected above it.
   *
   *  It used to be `delta_actionable` always — which is zero for every line item once you
   *  pick "Not yet rated" or "Withheld", so the whole row of cards silently disappeared
   *  exactly when you were trying to break that bucket down by charge. The cards measure
   *  the buckets you have selected; with nothing selected they measure the actionable
   *  difference, which is what the page headlines. */
  const itemValue = useCallback((r: EstimateGroupRow) => {
    // Unfiltered, a charge card answers "how much revenue should we expect from this",
    // which includes what is still pending a date — chassis is 97% pending, so the
    // actionable figure made the biggest charge on the book look like the smallest.
    // The row then adds up to the Total card rather than to Actionable.
    if (!kind.length && !state.length) return Number(r.delta_total ?? 0);
    let v = 0;
    if (kind.includes('missing')) v += Number(r.missing_total ?? 0);
    if (kind.includes('drift')) v += Number(r.drift_total ?? 0);
    if (kind.includes('overbilled')) v -= Number(r.overbilled_total ?? 0);
    if (state.includes('pending')) v += Number(r.pending_total ?? 0);
    if (kind.includes('withheld')) v += Number(r.withheld_total ?? 0);
    return v;
  }, [kind, state]);

  /** Toggle one line item's card. */
  const toggleItem = useCallback((item: string) => {
    setLiitem((cur) => (cur.includes(item) ? cur.filter((x) => x !== item) : [...cur, item]));
  }, []);

  const toggleKind = useCallback((...k: EstimateKind[]) => {
    setKind((cur) => {
      const same = cur.length === k.length && k.every((x) => cur.includes(x));
      // The kind cards count COMPLETED money only, so their filter has to say so.
      // Filtering on kind alone pulled in pending lines of the same kind: they added
      // $0 to the Missing column but still counted toward PROs and Changes, so the card
      // promised 5,490 lines and the table totalled 9,739.
      setState(same ? [] : ['completed']);
      return same ? [] : k;
    });
  }, []);

  /** Pending is a state, not a kind, so selecting it clears any kind selection rather
   *  than intersecting with it — "pending missing charges" is a drill-down, not a card. */
  const togglePending = useCallback(() => {
    setState((cur) => (cur.includes('pending') ? [] : ['pending']));
    setKind([]);
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Fetch a group's lines: the page query narrowed to that one group, whatever the
   *  axis (scopeQueryToGroup is exhaustive over the axes, so none can be forgotten). */
  const loadDrill = useCallback(async (row: EstimateGroupRow) => {
    const request = ++drillRequest.current;
    setDrillLoading(true);
    setDrillRows([]);
    const scoped = scopeQueryToGroup(query, groupBy, row.group_key);
    try {
      const res = await invoiceEstimateReportService.detail(scoped, {
        includeAgreed: includeMatched,
        additionalFields: additionalFieldsKey.split(',').filter(Boolean),
      });
      if (request !== drillRequest.current) return;
      setDrillRows(res.rows);
      setDrillMeta({ total: res.total, truncated: res.truncated });
    } catch (e: any) {
      if (request !== drillRequest.current) return;
      showToast({
        message: e?.response?.data?.detail || 'Could not load detail',
        type: 'error',
      });
    } finally {
      if (request === drillRequest.current) setDrillLoading(false);
    }
  }, [groupBy, query, includeMatched, additionalFieldsKey, showToast]);

  // THE DRILL-DOWN LOADS FROM ONE PLACE. Whatever changes what it should show — the
  // group opened, the matched-invoices setting, the columns added to it — lands here,
  // so there is no path that updates one input and forgets to refetch. Held until the
  // saved column selection is known: a deep link would otherwise fetch twice, once
  // bare and once with the columns, and show the first for a moment.
  useEffect(() => {
    if (!drill) return;
    // Spinner from the moment a group opens, even while the saved selection is still
    // being read — an empty grid in that window reads as "no lines", which is wrong.
    setDrillLoading(true);
    if (fieldSelection.ready) loadDrill(drill);
  }, [drill, fieldSelection.ready, loadDrill]);

  const openDrill = useCallback((row: EstimateGroupRow) => {
    setDrill(row);
    setDrillMeta({ total: 0, truncated: false });
    // Pushed, not replaced: this is what makes browser Back return to the summary.
    setSearchParams(
      { g: groupBy, k: row.group_key == null ? '' : String(row.group_key) },
      { replace: false },
    );
  }, [groupBy, setSearchParams]);

  /** Back to the report. The component never unmounts, so every filter is still set. */
  const closeDrill = useCallback(() => {
    setDrill(null);
    setSearchParams({}, { replace: false });
  }, [setSearchParams]);

  // Browser Back (or a hand-edited URL) drops the drill params — mirror that into state,
  // otherwise the detail view stays up while the URL says otherwise.
  useEffect(() => {
    if (!searchParams.get('k') && drill) setDrill(null);
  }, [searchParams, drill]);

  // Deep link / reload with ?g=&k= — reopen that group once the summary has loaded and
  // the row it refers to can be resolved.
  useEffect(() => {
    const k = searchParams.get('k');
    const g = searchParams.get('g');
    if (!k || drill || !summary || g !== groupBy) return;
    const row = summary.rows.find((r) => String(r.group_key ?? '') === k);
    if (row) setDrill(row);
  }, [searchParams, drill, summary, groupBy]);

  const kindOptions = useMemo<MultiSelectOption[]>(
    () => (Object.keys(KIND_META) as EstimateKind[]).map((k) => ({
      value: k, label: KIND_META[k].label,
    })),
    [],
  );

  /** What the drill-down table is actually showing, so the header above it can never
   *  quote a different population than the totals row below it. */
  const shown = useMemo(() => {
    let current = 0, suggested = 0, declined = 0;
    const pros = new Set<number>();
    for (const r of drillRows) {
      current += Number(r.current_total ?? 0);
      suggested += Number(r.projected ?? 0);
      declined += Number(r.withheld_total ?? 0);
      pros.add(r.pronumber);
    }
    return { pros: pros.size, current, suggested, declined, difference: suggested - current };
  }, [drillRows]);

  /** Cards worth showing, biggest mover first in whichever direction. */
  const visibleItemCards = useMemo(
    () => itemCards
      .filter((r) => Math.abs(itemValue(r)) >= 1)
      .sort((a, b) => Math.abs(itemValue(b)) - Math.abs(itemValue(a))),
    [itemCards, itemValue],
  );

  const totals = summary?.totals;
  const cards = cardTotals ?? totals;
  /** Is exactly this set of types currently selected? */
  const isKind = useCallback(
    (...k: EstimateKind[]) => kind.length === k.length && k.every((x) => kind.includes(x)),
    [kind],
  );
  const visibleActionable = useMemo(
    () => (summary?.rows ?? []).reduce((s, r) => s + Number(r.delta_actionable ?? 0), 0),
    [summary],
  );

  /** WHICH PROs a group row reports. With matched invoices excluded — the default — the
   *  PROs the estimate wants something changed on, because that is the population the
   *  drill-down opens on; the two used to say 1,030 here and 414 there, and beside 861
   *  changed lines the 1,030 read as a swapped column. With matched invoices included,
   *  every PRO in the group, matching a drill-down that then shows every line. */
  const prosShown = useCallback(
    (r: EstimateMeasures) => (includeMatched ? r.pros : r.changed_pros),
    [includeMatched],
  );

  const columns = useMemo<ColumnDef<EstimateGroupRow>[]>(() => [
    {
      accessorKey: 'group_label',
      header: GROUP_BY_OPTIONS.find((g) => g.value === groupBy)?.label ?? 'Group',
      cell: ({ row }) => {
        const label = row.original.group_label;
        if (groupBy === 'kind') {
          const meta = KIND_META[label as EstimateKind];
          return meta ? (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.tone}`}
                  title={meta.help}>
              {meta.label}
            </span>
          ) : label;
        }
        return <span className="font-medium">{label}</span>;
      },
    },
    {
      // Stable id whichever count is shown, so saved layouts and sorts hold.
      id: 'pros',
      accessorFn: prosShown,
      header: 'PROs',
      cell: ({ getValue }) => num(getValue() as number),
    },
    {
      // Why a row with no money on it is here: the estimate wants these lines changed.
      // A chassis line priced at $0 that should not be on the invoice is still a finding.
      // "Lines", in the header, because a PRO can carry several and the column beside it
      // counts PROs — unnamed, the two read as the same unit and looked swapped.
      accessorKey: 'change_lines',
      header: 'Changed lines',
      meta: { aggDefault: 'sum' },
      cell: ({ getValue }) => {
        const n = Number(getValue() ?? 0);
        return n ? num(n) : <span className="text-gray-300 dark:text-gray-600">—</span>;
      },
    },
    {
      accessorKey: 'current_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Current',
      cell: ({ getValue }) => <span className="font-mono">{money(getValue() as number)}</span>,
    },
    {
      accessorKey: 'projected_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Suggested',
      cell: ({ getValue }) => <span className="font-mono">{money(getValue() as number)}</span>,
    },
    {
      accessorKey: 'delta_actionable',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Actionable Difference',
      cell: ({ getValue }) => deltaSpan(getValue() as number),
    },
    {
      accessorKey: 'missing_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Missing',
      cell: ({ getValue }) => bucketMoney(getValue() as number, BUCKET_TONE.missing),
    },
    {
      accessorKey: 'drift_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Rate drift',
      cell: ({ getValue }) => signedMoney(getValue() as number),
    },
    {
      accessorKey: 'overbilled_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Billed, not owed',
      cell: ({ getValue }) => bucketMoney(getValue() as number, BUCKET_TONE.overbilled),
    },
    {
      accessorKey: 'pending_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Pending completion',
      cell: ({ getValue }) => (
        <span title={STATE_META.pending.help}>
          {bucketMoney(getValue() as number, BUCKET_TONE.pending)}
        </span>
      ),
    },
    {
      accessorKey: 'withheld_total',
      meta: { aggDefault: 'sum', aggType: 'money' },
      header: 'Withheld',
      cell: ({ getValue }) => (
        <span title={KIND_META.withheld.help}>
          {bucketMoney(getValue() as number, BUCKET_TONE.withheld)}
        </span>
      ),
    },
  ], [groupBy, prosShown]);

  const detailColumns = useMemo<ColumnDef<EstimateDetailRow>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'PRO',
      cell: ({ getValue }) => {
        const pro = getValue() as number;
        return (
          <button
            type="button"
            onClick={() => setSelectedPro(pro)}
            className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-1"
          >
            {pro}
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
          </button>
        );
      },
    },
    // registryTitle: false — the two names are the report's own composites (a customer
    // with no name reads as its number), so they keep their headers rather than taking
    // the registry's title for `customername` / `billtoname`; DETAIL_COVERED_FIELDS
    // still withholds those two from the picker. `billto` also shares its id with the
    // registry's bill-to NUMBER, which a title lookup by id would otherwise find.
    { accessorKey: 'customer', header: 'Customer', meta: { registryTitle: false } },
    { accessorKey: 'billto', header: 'Bill To', meta: { registryTitle: false } },
    { accessorKey: 'liitem', header: 'Line Item' },
    {
      accessorKey: 'kind',
      header: 'Type',
      cell: ({ row, getValue }) => {
        const k = getValue() as EstimateKind;
        const meta = KIND_META[k];
        const why = row.original.reason;
        return meta ? (
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.tone}`}
                title={why ? `${meta.help}\n\n${why}` : meta.help}>
            {meta.label}
          </span>
        ) : k;
      },
    },
    {
      accessorKey: 'current_total',
      header: 'Current',
      meta: { aggDefault: 'sum', aggType: 'money' },
      cell: ({ getValue }) => <span className="font-mono">{money2(getValue() as number)}</span>,
    },
    {
      accessorKey: 'projected',
      header: 'Suggested',
      meta: { aggDefault: 'sum', aggType: 'money' },
      // The `est` marker rides on the SUGGESTED figure because that is the projected one.
      // It was lost when the Why column went; without it the drill-down states a
      // projection from an assumed date in the same voice as a settled figure.
      cell: ({ row, getValue }) => (
        <span className="font-mono">
          {money2(getValue() as number)}
          {row.original.estimated === 'Y' && (
            <span
              className="ml-1 rounded bg-amber-100 px-1 text-[10px] uppercase tracking-wide text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"
              title={row.original.estimate_basis || 'Estimated from an assumed date, not a confirmation'}
            >
              est
            </span>
          )}
        </span>
      ),
    },
    {
      accessorKey: 'delta',
      header: 'Difference',
      meta: { aggDefault: 'sum', aggType: 'money' },
      cell: ({ getValue }) => deltaSpan(getValue() as number),
    },
    {
      // Per LINE, because that is where it is decided: the same PRO can be completed for
      // fuel and pending for chassis.
      id: 'state',
      accessorFn: (r: EstimateDetailRow) =>
        (r.estimated === 'Y' ? STATE_META.pending.label : STATE_META.completed.label),
      header: 'State',
      size: 110,
      cell: ({ row }) => {
        const st: EstimateState = row.original.estimated === 'Y' ? 'pending' : 'completed';
        const meta = STATE_META[st];
        return (
          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.tone}`}
                title={st === 'pending'
                  ? `${meta.help}\n\n${row.original.estimate_basis || ''}`.trim()
                  : meta.help}>
            {meta.label}
          </span>
        );
      },
    },
    {
      // WHAT THE OVERRIDE DECLINED. Current and Suggested are both $0 on a withheld line
      // and that is correct — the money is deliberately forgone, so it must never count
      // as revenue the invoice will bill. But the AMOUNT is the whole point of recording
      // the decision, and without it the row reads as a charge worth nothing rather than
      // a charge worth $125 that someone chose not to bill. The per-PRO modal says
      // "REJECTED CHARGES — $225.00 not billed"; this is the same fact, per line.
      accessorKey: 'withheld_total',
      header: 'Not Billed',
      meta: { aggDefault: 'sum', aggType: 'money' },
      cell: ({ row }) => {
        const v = row.original.withheld_total;
        if (v == null || Number(v) === 0) {
          return <span className="text-gray-300 dark:text-gray-600">—</span>;
        }
        const qty = row.original.withheld_qty;
        const rate = row.original.withheld_rate;
        const basis = qty != null && rate != null
          ? `${qty} × ${money2(rate)} = ${money2(v)}` : undefined;
        return (
          <span className="font-mono text-amber-700 dark:text-amber-400" title={basis}>
            {money2(v)}
          </span>
        );
      },
    },
    {
      // OVERRIDE REASON ONLY — a person's recorded decision not to bill, from the Override
      // Reasons list (cms.invoice_lineitem_override). The engine's own wording ("update
      // fuel surcharge rate/total") is machine narration, not a reason anyone chose, and
      // under this heading it read as though someone had written it. It still travels with
      // the row: it is the Type chip's tooltip, where it explains the classification it
      // actually belongs to. Blank here means nobody has recorded a decision.
      id: 'reason',
      accessorFn: (r: EstimateDetailRow) => r.override_reason || '',
      header: 'Override Reason',
      size: 220,
      cell: ({ row }) => {
        const r = row.original;
        if (!r.override_reason) return <span className="text-gray-300 dark:text-gray-600">—</span>;
        const when = r.override_at
          ? ` on ${new Date(String(r.override_at).replace(' ', 'T')).toLocaleDateString()}`
          : '';
        return (
          <span
            title={`${r.override_reason}\nInvoice override, set by ${r.override_by || 'unknown'}${when}`}
            className="inline-flex items-center gap-1 max-w-full rounded px-1.5 py-0.5 text-xs bg-amber-50 text-amber-900 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-100 dark:border-amber-700"
          >
            <LockClosedIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{r.override_reason}</span>
          </span>
        );
      },
    },
  ], []);

  // The grid's own columns plus one per added registry field, and the picker universe
  // that offers the rest. A hook, so it sits above the early return below. Only the
  // drill-down gets this: the summary is grouped, and an order field has no meaning on
  // a customer row.
  const { columns: drillColumns, availableFields } = useAdditionalFieldColumns<EstimateDetailRow>(
    fieldSelection, { baseColumns: detailColumns, coveredFields: DETAIL_COVERED_FIELDS },
  );

  const asOf = totals?.synced_at
    ? new Date(String(totals.synced_at).replace(' ', 'T')).toLocaleString()
    : null;

  // DRILL-DOWN AS A FULL PAGE, NOT A MODAL. A group can hold well over a thousand lines
  // (Caterpillar: 1,187 that differ), and a dialog fights that much table — the column
  // headers scroll under the dialog's own header and the totals row lands off-screen.
  // Same route, same mounted component, so the summary's filters are untouched on return;
  // the drill selection lives in the query string purely so browser Back works and the
  // view can be linked to someone else.
  if (drill) {
    return (
      <div className="p-4 flex flex-col gap-3 h-[calc(100vh-7rem)] overflow-hidden">
        <button
          type="button"
          onClick={closeDrill}
          className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to report
        </button>

        <div className="shrink-0 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {drill.group_label}
            </h1>
            {/* The cards do not come down here, so the filters they set would otherwise
                vanish, leaving a line count with no indication of what it counted. These
                chips STATE the scope and nothing more: filters are set on the report, the
                same reason the matched-invoices toggle is not repeated here. One place to
                change them, so the two views cannot disagree about what is in scope. */}
            {(kind.length > 0 || liitem.length > 0 || state.length > 0) && (
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <span className="text-xs text-gray-400 dark:text-gray-500">Filtered to</span>
                {state.map((st) => (
                  <span key={st} title={STATE_META[st].help}
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATE_META[st].tone}`}>
                    {STATE_META[st].label}
                  </span>
                ))}
                {kind.map((k) => (
                  <span key={k} title={KIND_META[k]?.help}
                        className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${KIND_META[k]?.tone ?? ''}`}>
                    {KIND_META[k]?.label ?? k}
                  </span>
                ))}
                {liitem.map((it) => (
                  <span key={it}
                        className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">
                    {it}
                  </span>
                ))}
              </div>
            )}
            {/* THE HEADER DESCRIBES THE ROWS BELOW IT, and is computed from them. It used
                to carry the group's own totals — every line on every PRO, agreed ones
                included, with an "actionable" figure that excluded unrated — while the
                table showed only the lines that differ. Both were right and they disagreed
                on screen ($11,026 vs a $1,184 sum), which is the one thing a summary must
                never do. Derived from `drillRows`, it now matches the table's totals row by
                construction, whatever is filtered or toggled. */}
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {num(shown.pros)} PROs · current {money(shown.current)} · suggested{' '}
              {money(shown.suggested)} ·{' '}
              <span className={`font-medium ${
                shown.difference < 0
                  ? 'text-purple-700 dark:text-purple-300'
                  : 'text-gray-700 dark:text-gray-200'
              }`}>
                {shown.difference > 0 ? '+' : ''}{money(shown.difference)} difference
              </span>
              {shown.declined > 0 && (
                <> · <span className="text-amber-700 dark:text-amber-400">
                  {money(shown.declined)} declined
                </span></>
              )}
            </p>
            {(drillLoading || drillMeta.truncated) && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {drillLoading ? 'Loading lines…' : (
                  <span className="text-amber-600 dark:text-amber-400">
                    Showing the largest {num(drillRows.length)} of {num(drillMeta.total)} differences
                    . Narrow the filters to see the rest
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          <TanTable
            data={drillRows}
            columns={drillColumns}
            // The column picker offers every orders-registry field; a pick is saved
            // under the grid's key and refetches the lines with that field attached.
            availableFields={availableFields}
            additionalFields={fieldSelection.additionalFields}
            onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
            loading={drillLoading}
            settingsKey={DETAIL_SETTINGS_KEY}
            searchPlaceholder="Search lines…"
            enableColumnFilters={true}
            enableGrouping={true}
            fillContainer
            // Same click-to-highlight as the LC dashboards: a PRO you have opened stays
            // marked, with a ring on the last one. Matters more here than most grids —
            // one PRO can appear on several rows (a missing charge AND a rate drift), so
            // without it there is no way to tell which you have already worked.
            trackViewedColumnId="pronumber"
          />
        </div>

        <ProDetailSlideOver
          pronumber={selectedPro}
          isOpen={selectedPro !== null}
          onClose={() => setSelectedPro(null)}
          // Every PRO here is opened because of something on its invoice, so land on the
          // Invoicing tab rather than making the reader navigate there themselves.
          initialTab="invoicing"
        />
      </div>
    );
  }

  return (
    <div className="p-4 flex flex-col gap-3 h-[calc(100vh-7rem)] overflow-hidden">
      <div className="shrink-0 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Invoice Reconciliation
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-3xl">
            What the open book will bill once every earned charge is on it, against what it
            bills today: charges missing from the invoice, rates that no longer match the
            quote, and charges billed that aren't owed. Open orders only, so this is
            exposure you can still prevent.
          </p>
        </div>
        <div className="text-right text-xs text-gray-500 dark:text-gray-400">
          {asOf && <div>Swept {asOf}</div>}
          {totals && <div>{num(totals.pros)} open PROs · {num(totals.line_count)} projected lines</div>}
        </div>
      </div>

      {/* FILTERS FIRST: the date window scopes every card below it, so the page reads
          top-down — choose the window, then choose what to look at. Group by belongs here
          too; the type and line-item selections ARE the cards. */}
      <FilterBar wrap label="Filters">
        {/* Delivery date = the delivery leg's arrival. Both ends optional on purpose:
            clear/clear is every open invoice, and an open END also pulls in containers
            that have not delivered yet — 72% of the open book, and the ones still most
            worth fixing, since nothing has been billed on them. */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
            Delivered
          </span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={deliveredFrom}
            onChange={(val) => setDeliveredFrom((val as Date | null) ?? null)}
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="From"
          />
          <span className="text-xs text-gray-400">to</span>
          <DateTimePicker
            mode="date"
            selectionType="single"
            value={deliveredTo}
            onChange={(val) => setDeliveredTo((val as Date | null) ?? null)}
            inputVariant="compact"
            inputClassName="w-32"
            labelType="none"
            placeholder="Open"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">Group by</span>
          <SingleSelect
            options={GROUP_BY_OPTIONS}
            value={groupBy}
            onChange={(v) => setGroupBy(v as EstimateGroupBy)}
            size="sm"
            className="w-44"
            usePortal
          />
        </div>
        {/* Both toggles are set once here and inherited by every drill-down — the
            drill-down has no controls of its own, so there is one place to look and
            one to change. */}
        <div className="flex items-center gap-2">
          <Toggle
            checked={showNotAvailable}
            onChange={setShowNotAvailable}
            size="sm"
            ariaLabel="Show not available"
          />
          <span
            className="text-xs text-gray-600 dark:text-gray-300 select-none whitespace-nowrap"
            title="Also show orders that can't move yet: imports and exports with no GWI Available date, and other move types whose originate leg has no appointment. Their charges aren't due yet, so they're hidden by default."
          >
            Show not available
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Toggle
            checked={includeMatched}
            onChange={setIncludeMatched}
            size="sm"
            ariaLabel="Include matched invoices"
          />
          <span
            className="text-xs text-gray-600 dark:text-gray-300 select-none whitespace-nowrap"
            title="Groups where every line already matches the estimate: nothing proposed, nothing to chase."
          >
            Include matched invoices
          </span>
        </div>
        {(kind.length > 0 || liitem.length > 0) && (
          <button
            type="button"
            onClick={() => { setKind([]); setLiitem([]); setState([]); }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Clear card filters
          </button>
        )}
      </FilterBar>

      {cards && (
        <div className="shrink-0 grid grid-cols-3 md:grid-cols-4 xl:grid-cols-7 gap-2">
          <Tile
            label="Actionable"
            value={money(cards.delta_actionable)}
            tone="red"
            hero
            help="Missing plus rate drift, less what is billed but not owed, counting only lines whose dates are confirmed. The money you can chase today."
            onClick={() => toggleKind('missing', 'drift', 'overbilled')}
            active={isKind('missing', 'drift', 'overbilled')}
          />
          <Tile label="Missing" value={money(cards.missing_total)} tone="red"
                help={KIND_META.missing.help}
                onClick={() => toggleKind('missing')} active={isKind('missing')} />
          <Tile label="Rate drift" value={money(cards.drift_total)} tone="amber"
                help={KIND_META.drift.help}
                onClick={() => toggleKind('drift')} active={isKind('drift')} />
          <Tile label="Billed, not owed" value={money(cards.overbilled_total)} tone="purple"
                help={KIND_META.overbilled.help}
                onClick={() => toggleKind('overbilled')} active={isKind('overbilled')} />
          <Tile label="Withheld" value={money(cards.withheld_total)} tone="blue"
                help={KIND_META.withheld.help}
                onClick={() => toggleKind('withheld')} active={isKind('withheld')} />
          <Tile label="Pending completion" value={money(cards.pending_total)} tone="teal"
                help={STATE_META.pending.help}
                onClick={togglePending} active={state.includes('pending')} />
          <Tile label="Total" value={money(cards.delta_total)} tone="indigo"
                help="Suggested minus current across every bucket, completed and pending together. Actionable plus pending completion." />
        </div>
      )}

      {/* PER LINE ITEM — the cut that turns a total into an action, because it names the
          charge going unbilled (CHASSIS MANAGEMENT and YARD STORAGE carry most of it).
          Ranked by |actionable| so the biggest movers lead in either direction. */}
      {visibleItemCards.length > 0 && (
        <div className="shrink-0 flex flex-wrap gap-2 max-h-24 overflow-y-auto">
          {visibleItemCards.map((r) => {
            const key = String(r.group_key ?? '');
            const on = liitem.includes(key);
            return (
              <button
                key={key}
                type="button"
                onClick={() => toggleItem(key)}
                aria-pressed={on}
                // The figure on the card is the ACTIONABLE difference, which counts
                // completed lines only. Quoting current and suggested across every state
                // beside it left the two irreconcilable: chassis reads $26,375 against a
                // $1.02M gap, because $998,613 of that gap is pending a terminate date.
                // The tooltip now shows the split that explains the number.
                title={`${r.group_label}\n${num(prosShown(r))} PROs · current ${money(r.current_total)} · suggested ${money(r.projected_total)}`
                  + `\n\nActionable now: ${money(itemValue(r))}`
                  + (Number(r.pending_total)
                      ? `\nPending completion: ${money(r.pending_total)} (waiting on real dates)` : '')}
                className={`relative overflow-hidden rounded-md border pl-2.5 pr-2.5 py-1.5 text-left transition ${
                  on
                    ? 'bg-indigo-600 border-indigo-600 text-white shadow-md ring-2 ring-offset-1 ring-indigo-700 dark:ring-offset-gray-900'
                    : 'border-gray-200 bg-gradient-to-br from-gray-100 to-white hover:from-indigo-50 hover:border-indigo-300 hover:shadow-sm dark:border-gray-700 dark:from-gray-800 dark:to-gray-900'
                }`}
              >
                <div className={`text-[10px] uppercase tracking-wide max-w-[11rem] truncate flex items-center gap-1 ${
                  on ? 'text-white/90' : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {on && <CheckCircleIcon className="w-3 h-3 shrink-0" aria-hidden="true" />}
                  <span className="truncate">{r.group_label}</span>
                </div>
                <div className={`font-mono text-sm font-bold ${
                  on
                    ? 'text-white'
                    : itemValue(r) < 0
                    ? 'text-purple-700 dark:text-purple-300'
                    : 'text-gray-900 dark:text-gray-100'
                }`}>
                  {money(itemValue(r))}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {summary && totals && Math.abs(Number(totals.delta_actionable) - visibleActionable) > 1 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Showing {num(summary.rows.length)} groups worth {money(visibleActionable)} of the{' '}
          {money(totals.delta_actionable)} total actionable exposure.
        </p>
      )}

      <div className="flex-1 min-h-0">
        <TanTable
          data={summary?.rows ?? []}
          columns={columns}
          loading={loading}
          errorMessage={error}
          onRetry={load}
          settingsKey="invoice-estimate-report"
          searchPlaceholder="Search groups…"
          onRowClick={openDrill}
          enableColumnFilters={true}
          fillContainer
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
}

function InvoiceEstimateReport() {
  return (
    <ViewedProsProvider>
      <InvoiceEstimateReportContent />
    </ViewedProsProvider>
  );
}

export default InvoiceEstimateReport;
